import {MediaGalleryListResponseType} from "../types/MediaGalleryListResponseType";
import {MediaGalleryConfigResponseType} from "../types/MediaGalleryConfigResponseType";
import {MediaGalleryFilterType} from "@src/Resources/epim/Templates/MediaGallery/types/MediaGalleryFilterType";
import {GlobalFilterOptionType, GlobalFilterType, GlobalFilterTypesType} from "@global/global-elements/global-filter/GlobalFilterType";
import type {Productfinder} from "@src/_types/productfinder";
import {MediaGalleryStateModeEnum} from "@src/Resources/epim/Templates/MediaGallery/store/MediaGalleryStateType";
import {helper} from "@src/Resources/epim/Partials/PimEndproduktFinder/shared/helper";
import {MediaGalleryCollectionResponseType} from "@src/Resources/epim/Templates/MediaGallery/types/MediaGalleryCollectionResponseType";

const serializeFilters = (filters: GlobalFilterType[]): string => {
    const filterMap: {[name: string]: string[]} = {};
    filters.forEach((filter) => {
        filter.options.forEach(option => {
            if (!option.selected) { return }
            const selectedFilter = option.parentFilter ?? filter.name;
            const existingValue = filterMap[selectedFilter];
            if (existingValue) {
                if (!existingValue.includes(option.value)) {
                    existingValue.push(option.value)
                }
            }
            else {
                filterMap[selectedFilter] = [option.value]
            }
        })
    })

    return helper.generateHash(filterMap);

    // return Array.from(filterMap.entries()).map(([key, value]) => key + ':' + value.join("#")).join(";")
}
let searchController: AbortController | null = null

const search = (query: string, filter: GlobalFilterType[], rows = 12, start = 0, mode: MediaGalleryStateModeEnum): Promise<MediaGalleryListResponseType> => {
    return new Promise((resolve, reject) => {
        // Prevent duplicate requests
        if (searchController !== null) {
            searchController.abort('search was called again')
        }
        searchController = new AbortController();

        const url = new URL(mode === MediaGalleryStateModeEnum.Search ? window.MEDIAGALLERY_API_LIST : window.MEDIAGALLERY_API_COLLECTION);

        url.searchParams.set('tx_epim_mediagallery[query]', query)
        url.searchParams.set('tx_epim_mediagallery[filter]', serializeFilters(filter))
        url.searchParams.set('tx_epim_mediagallery[rows]', rows.toString())
        url.searchParams.set('tx_epim_mediagallery[start]', start.toString())

        fetch(url.href, {signal: searchController.signal})
            .then(result => {
                result.json().then(data => {
                    resolve(data as MediaGalleryListResponseType)
                })
            })
            .catch(error => {
                reject(error)
            })
            .finally(() => {
                searchController = null
            })
    })
}

// noinspection TypeScriptValidateTypes
const collectionAdd = (ids: string[]): Promise<MediaGalleryCollectionResponseType> => {
    return new Promise((resolve, reject) => {
        const url = new URL(window.MEDIAGALLERY_API_COLLECTION)
        url.searchParams.set('tx_epim_mediagallery[pimId]', ids.join(","))

        fetch(url.href, {method: 'PATCH'})
            .then(result => {
                if (result.status === 200) {
                    result.json().then(data => {
                        resolve(data as MediaGalleryCollectionResponseType)
                    })
                } else {
                    result.json().then(errorMessage => {
                        console.error('collectionAdd', errorMessage)
                        reject(false)
                    })
                }
            })
            .catch(error => {
                console.error('collectionAdd', url, error)
                reject(error)
            })
    })
}

const collectionRemove = (id: string): Promise<MediaGalleryCollectionResponseType> => {
    return new Promise((resolve, reject) => {
        const url = new URL(window.MEDIAGALLERY_API_COLLECTION)
        url.searchParams.set('tx_epim_mediagallery[pimId]', id)

        fetch(url.href, {method: 'DELETE'})
            .then(result => {
                if (result.status === 200) {
                    result.json().then(data => {
                        resolve(data as MediaGalleryCollectionResponseType)
                    })
                } else {
                    console.error('collectionDelete', result.status, result.statusText)
                    reject(false)
                }
            })
            .catch(error => {
                console.error('collectionDelete', url, error)
                reject(error)
            })
    })
}

let configController: AbortController | null = null
const config = (): Promise<MediaGalleryConfigResponseType> => {
    return new Promise((resolve, reject) => {
        // Prevent duplicate requests
        if (configController !== null) {
            reject('config is already loading');
            return;
        }
        configController = new AbortController();

        const url = new URL(window.MEDIAGALLERY_API_CONFIG)

        fetch(url.href, {signal: configController.signal})
            .then(result => {
                result.json().then(data => {
                    resolve(data as MediaGalleryConfigResponseType)
                })
            })
            .catch(error => {
                console.error(error)
                reject(error)
            })
            .finally(() => {
                configController = null
            })
    })
}

const setAvailableOptions = (filters: MediaGalleryFilterType[], available: { [p: string]: string[] }): MediaGalleryFilterType[] => {
    return filters.map(filter => {
        return Object.assign({}, filter, {
            options: filter.options.map(option => {
                if (option.style === "nested" && option.parentFilter) {
                    return Object.assign({}, option, {disabled: !available[option.parentFilter]?.includes(option.value) ?? true})
                }
                return Object.assign({}, option, {disabled: !available[filter.name]?.includes(option.value) ?? true})
            })
        })
    })
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const setSelectedOptions = (filters: MediaGalleryFilterType[], selection: { [p: string]: string[] }): MediaGalleryFilterType[] => {
    return filters.map(filter => {
        return Object.assign({}, filter, {
            options: filter.options.map(option => {
                if (option.style === "nested" && option.parentFilter) {
                    return Object.assign({}, option, {selected: selection[option.parentFilter]?.includes(option.value) ?? false})
                }
                return Object.assign({}, option, {selected: selection[filter.name]?.includes(option.value) ?? false})
            })
        })
    })
}

const convertFilterToOption = (filter: Productfinder.Filter): GlobalFilterOptionType[] => {
    const options: GlobalFilterOptionType[] = []

    options.push({
        key: filter.name,
        value: filter.name,
        title: filter.title,
        selected: false,
        disabled: true,
        style: "group",
    })

    const convertedOptions = filter.options.map((option: Productfinder.FilterOption) => {
        return {
            key: filter.name + "-" + option,
            title: option,
            value: option,
            selected: false,
            disabled: false,
            parentFilter: filter.name,
            style: "nested"
        } as GlobalFilterOptionType
    })

    options.push(...convertedOptions)

    return options
}

const convertProductFinderFilterstToMediaGalleyFilters = (filters: Productfinder.Filter[]): MediaGalleryFilterType[] => {
    const converted: MediaGalleryFilterType[] = [];
    let convertedOptions: GlobalFilterOptionType[];

    const mapFilterOptionFn = (option: Productfinder.FilterOption) => {
        if (typeof option === "string") {
            return {
                key: option,
                value: option,
                title: option,
                selected: false,
                disabled: false,
            } satisfies GlobalFilterOptionType
        } else {
            return {
                key: option.value,
                value: option.value,
                title: option.title,
                selected: false,
                disabled: false,
            } satisfies GlobalFilterOptionType
        }
    }

    filters.forEach(filter => {
        if (globalFilterTypeFromString(filter.type) === GlobalFilterTypesType.GROUPED_SELECTION) {
            const options: GlobalFilterOptionType[] = []

            filter.options.forEach((option: Productfinder.FilterOption) => {
                const resolvedOption = filters.find(filter => filter.name === option)
                if (resolvedOption) {
                    options.push(...convertFilterToOption(resolvedOption))
                }
                else { // For "All ..."
                    options.push(mapFilterOptionFn(option))
                }
            })

            convertedOptions = options
        } else {
            convertedOptions = filter.options.map(mapFilterOptionFn)
        }

        const resolvedType = globalFilterTypeFromString(filter.type)
        if (GlobalFilterTypesType.GROUPED_SELECTION === resolvedType || GlobalFilterTypesType.SELECT === resolvedType) {
            converted.push({
                name: filter.name,
                title: filter.title,
                type: resolvedType,
                options: convertedOptions,
                default: filter.default,
            })
        }
    })

    return converted
}

const globalFilterTypeFromString = (input: string): GlobalFilterTypesType => {
    switch (input) {
        case GlobalFilterTypesType.GROUPED_SELECTION:
            return GlobalFilterTypesType.GROUPED_SELECTION;
        case GlobalFilterTypesType.SELECT:
            return GlobalFilterTypesType.SELECT
        case GlobalFilterTypesType.SELECTION_GROUP:
            return GlobalFilterTypesType.SELECTION_GROUP
        default:
            return GlobalFilterTypesType.UNDEFINED
    }
}

const download = (pim_ids: string[]): Promise<boolean> => {
    return new Promise((resolve) => {
        const url = new URL(window.MEDIAGALLERY_API_DOWNLOAD)
        url.searchParams.set('tx_epim_mediagallery[ids]', pim_ids.join(','))

        document.location.href = url.href

        // Fake timeout to prevent download Hsmmering
        setTimeout(() => {
            resolve(true)
        }, Math.min(1000 * pim_ids.length, 5000))
    })
}

export const mediaGalleryService = {
    search,
    collectionAdd,
    collectionRemove,
    config,
    setAvailableOptions,
    setSelectedOptions,
    convertProductFinderFilterstToMediaGalleyFilters,
    download
}
